import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaCertificate, FaAward, FaBookOpen } from 'react-icons/fa';
import { SectionHeader, Card, Badge, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import { personalInfo, education, certifications } from '../data/profileData';

const Education = () => {
  const additionalTraining = [
    {
      title: 'ERPNext Customization & Development',
      provider: 'Self-paced / Professional Development',
      description: 'Advanced ERPNext customization, Frappe Framework development, and enterprise implementation.',
    },
    {
      title: 'Blockchain Technology',
      provider: 'Professional Development',
      description: 'Blockchain fundamentals, smart contracts, and decentralized application development.',
    },
    {
      title: 'Python for Data Science',
      provider: 'Professional Development',
      description: 'Python programming, data analysis with Pandas, and machine learning fundamentals.',
    },
    {
      title: 'AWS Cloud Practitioner',
      provider: 'Amazon Web Services',
      description: 'Cloud computing fundamentals, AWS services, and cloud architecture best practices.',
    },
    {
      title: 'Cybersecurity Fundamentals',
      provider: 'Professional Development',
      description: 'Network security, vulnerability assessment, penetration testing, and security frameworks.',
    },
    {
      title: 'Moodle LMS Administration',
      provider: 'Moodle Academy',
      description: 'Moodle setup, administration, course design, and plugin development.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Education | Rakesh Kumar Tripathi</title>
        <meta name="description" content={`Educational background and certifications of ${personalInfo.name}. Microsoft Certified Professional, IIT Kharagpur certified in High Performance Computing.`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-8 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeader
              badge="Education"
              title="Academic Background"
              subtitle="Strong foundation in computer science combined with industry-recognized certifications and continuous professional development."
            />
          </AnimatedSection>
        </div>
      </section>

      {/* Education Timeline Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold text-dark-800 mb-8 text-center">Academic Education</h2>

          <div className="max-w-3xl mx-auto space-y-6">
            {education.map((edu, index) => (
              <motion.div
                key={edu.id}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card elevated>
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FaGraduationCap className="w-8 h-8 text-primary-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <h3 className="text-lg font-bold text-dark-800">{edu.degree}</h3>
                        <Badge variant="primary">{edu.year}</Badge>
                      </div>
                      <p className="text-primary-500 font-medium mb-1">{edu.institution}</p>
                      <p className="text-dark-400 text-sm mb-3">{edu.location}</p>
                      <p className="text-dark-500 text-sm mb-4">{edu.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {edu.highlights.map((highlight) => (
                          <Badge key={highlight} variant="outline" size="sm">
                            {highlight}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section-padding gradient-bg-subtle">
        <div className="container-custom">
          <SectionHeader
            badge="Credentials"
            title="Professional Certifications"
            subtitle="Industry-recognized certifications validating expertise"
          />

          <StaggerContainer className="grid md:grid-cols-3 gap-6">
            {certifications.map((cert) => (
              <StaggerItem key={cert.id}>
                <Card elevated className="h-full text-center">
                  <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                    <FaCertificate className="w-10 h-10 text-white" />
                  </div>
                  <Badge variant="secondary" className="mb-3">{cert.year}</Badge>
                  <h3 className="text-lg font-bold text-dark-800 mb-2">{cert.name}</h3>
                  <p className="text-primary-500 font-medium text-sm mb-3">{cert.issuer}</p>
                  <p className="text-dark-500 text-sm">{cert.description}</p>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* IIT Kharagpur Highlight */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-secondary-500">
        <div className="container-custom">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
              <FaAward className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              IIT Kharagpur Certified
            </h2>
            <p className="text-white/90 text-lg mb-6">
              STC Certification in High Performance Computing and Enterprise Computing
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              {['AS400', 'COBOL', 'JCL', 'Enterprise Computing'].map((tech, index) => (
                <motion.div
                  key={tech}
                  className="bg-white/20 backdrop-blur rounded-lg px-4 py-3"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <span className="text-white font-medium">{tech}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Additional Training Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader
            badge="Continuous Learning"
            title="Professional Development"
            subtitle="Ongoing training and skill development to stay current with technology"
          />

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {additionalTraining.map((training, index) => (
              <StaggerItem key={index}>
                <Card hover className="h-full">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                      <FaBookOpen className="w-5 h-5 text-primary-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-dark-800 text-sm">{training.title}</h3>
                      <p className="text-primary-500 text-xs">{training.provider}</p>
                    </div>
                  </div>
                  <p className="text-dark-500 text-sm">{training.description}</p>
                </Card>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Teaching Experience Highlight */}
      <section className="section-padding bg-dark-800">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge variant="secondary" className="mb-4">Academic Experience</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Former Assistant Professor
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Beyond certifications, I have hands-on experience as an educator. As an Assistant Professor,
                I taught various computer science subjects and mentored numerous students who have gone on
                to successful careers in technology.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {['C/C++', 'Java', 'Python', 'Data Structures', 'Computer Networks', 'Operating Systems'].map((subject) => (
                  <div key={subject} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-secondary-400 rounded-full" />
                    <span className="text-gray-300 text-sm">{subject}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {[
                { value: '1000+', label: 'Students Taught' },
                { value: '6+', label: 'Years Teaching' },
                { value: '10+', label: 'Subjects Covered' },
                { value: '5+', label: 'Batches Mentored' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="bg-dark-700 rounded-xl p-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="text-3xl font-bold text-primary-400 mb-1">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Education;
